import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, MenuItem, Grid, Divider, Badge, Avatar, InputAdornment,
  Tabs, Tab, Autocomplete,
} from '@mui/material'
import {
  Add, Delete, Assignment, Search, LocalShipping, Person, Phone,
  LocationOn, Flag, AccessTime, FilterList, Visibility, Edit,
  ArrowForward, Schedule, DirectionsCar, CheckCircle, Cancel,
  PendingActions, Route as RouteIcon, Notes,
} from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { format, formatDistanceToNow } from 'date-fns'
import { useJobs, useCreateJob, useDeleteJob, useUpdateJobStatus, useAssignJob, Job } from '../../hooks/useFleet'
import { useDevices, Device } from '../../hooks/useDevices'
import { useUsers, User } from '../../hooks/useUsers'
import { ConfirmDialog } from '../../components/ConfirmDialog'

// ── Constants ────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { bg: string; color: string; border: string; icon: React.ReactElement; label: string }> = {
  pending:   { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', icon: <PendingActions sx={{ fontSize: 14 }} />, label: 'Pending' },
  assigned:  { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe', icon: <Person sx={{ fontSize: 14 }} />,         label: 'Assigned' },
  en_route:  { bg: '#fffbeb', color: '#f59e0b', border: '#fde68a', icon: <LocalShipping sx={{ fontSize: 14 }} />,  label: 'En Route' },
  completed: { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0', icon: <CheckCircle sx={{ fontSize: 14 }} />,    label: 'Completed' },
  cancelled: { bg: '#fff1f2', color: '#ef4444', border: '#fecaca', icon: <Cancel sx={{ fontSize: 14 }} />,         label: 'Cancelled' },
}
const STATUS_OPTIONS = Object.keys(STATUS_CONFIG)
const PRIORITY_OPTIONS = ['low', 'normal', 'high', 'urgent'] as const
const PRIORITY_COLORS: Record<string, string> = { low: '#94a3b8', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' }

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

// ── Job Detail Dialog ────────────────────────────────────────────────

function JobDetailDialog({ job, onClose, devices, users }: {
  job: Job; onClose: () => void
  devices: Device[]; users: User[]
}) {
  const { t } = useTranslation()
  const setStatus = useUpdateJobStatus()
  const assign = useAssignJob()
  const [driverId, setDriverId] = useState(job.assigned_driver_id ?? '')
  const [deviceId, setDeviceId] = useState(job.assigned_device_id ?? '')

  const driverName = users.find((u) => u.id === job.assigned_driver_id)?.name
  const deviceName = devices.find((d) => d.id === job.assigned_device_id)?.name

  const handleAssign = async () => {
    await assign.mutateAsync({ id: job.id, driver_id: driverId || undefined, device_id: deviceId || undefined })
  }

  const s = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.pending

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            {job.reference || `JOB-${job.id.slice(0, 8).toUpperCase()}`}
          </Typography>
          <Typography variant="caption" color="#94a3b8">
            Created {format(new Date(job.created_at), 'PPpp')}
          </Typography>
        </Box>
        <Chip icon={s.icon} label={s.label} size="small" sx={{
          bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
          fontWeight: 600, fontSize: 12,
        }} />
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={3}>
          {/* Left column — Job info */}
          <Grid item xs={12} md={7}>
            <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em' }}>
              {t('jobs.customerName')}
            </Typography>
            <Card sx={{ p: 2, mt: 1, border: '1px solid #f1f5f9', borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: '#f1f5f9', color: '#64748b' }}>
                    <Person sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#1e293b">
                      {job.customer_name || '—'}
                    </Typography>
                    {job.customer_phone && (
                      <Typography variant="caption" color="#64748b">
                        <Phone sx={{ fontSize: 10, mr: 0.3, verticalAlign: 'middle' }} />
                        {job.customer_phone}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </Card>

            <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mt: 2.5, display: 'block' }}>
              {t('jobs.pickupAddress')}
            </Typography>
            <Card sx={{ p: 2, mt: 1, border: '1px solid #f1f5f9', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981', border: '2px solid #d1fae5' }} />
                    <Box sx={{ width: 2, flex: 1, bgcolor: '#e2e8f0', my: 0.5 }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444', border: '2px solid #fecaca' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="caption" color="#94a3b8" fontWeight={600} sx={{ fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {t('jobs.pickupAddress')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">
                        {job.pickup_address || '—'}
                      </Typography>
                      {job.pickup_latitude != null && (
                        <Typography variant="caption" color="#94a3b8" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                          {job.pickup_latitude.toFixed(5)}, {job.pickup_longitude?.toFixed(5)}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight={600} sx={{ fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {t('jobs.dropoffAddress')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="#1e293b">
                        {job.dropoff_address || '—'}
                      </Typography>
                      {job.dropoff_latitude != null && (
                        <Typography variant="caption" color="#94a3b8" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                          {job.dropoff_latitude.toFixed(5)}, {job.dropoff_longitude?.toFixed(5)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Card>

            {job.notes && (
              <>
                <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mt: 2.5, display: 'block' }}>
                  {t('jobs.notes')}
                </Typography>
                <Card sx={{ p: 2, mt: 1, border: '1px solid #f1f5f9', borderRadius: 2 }}>
                  <Typography variant="body2" color="#475569" sx={{ whiteSpace: 'pre-wrap' }}>
                    {job.notes}
                  </Typography>
                </Card>
              </>
            )}
          </Grid>

          {/* Right column — Assignment & Actions */}
          <Grid item xs={12} md={5}>
            <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em' }}>
              {t('jobs.assignDriver')}
            </Typography>
            <Card sx={{ p: 2, mt: 1, border: '1px solid #f1f5f9', borderRadius: 2 }}>
              <Stack spacing={2}>
                {driverName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ fontSize: 16, color: '#3b82f6' }} />
                    <Typography variant="body2" color="#1e293b">{t('jobs.assignDriver')}: <strong>{driverName}</strong></Typography>
                  </Box>
                )}
                {deviceName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCar sx={{ fontSize: 16, color: '#34A8E0' }} />
                    <Typography variant="body2" color="#1e293b">{t('jobs.assignVehicle')}: <strong>{deviceName}</strong></Typography>
                  </Box>
                )}
                <Divider />
                <TextField select size="small" label={t('jobs.assignDriver')} value={driverId}
                  onChange={(e) => setDriverId(e.target.value)} fullWidth
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">{t('common.noData')}</MenuItem>
                  {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label={t('jobs.assignVehicle')} value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)} fullWidth
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">{t('common.noData')}</MenuItem>
                  {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
                <Button variant="contained" size="small" onClick={handleAssign}
                  disabled={assign.isPending}
                  sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
                  {assign.isPending ? t('common.loading') : t('jobs.assignDriver')}
                </Button>
              </Stack>
            </Card>

            <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mt: 2.5, display: 'block' }}>
              {t('common.status')}
            </Typography>
            <Card sx={{ p: 2, mt: 1, border: '1px solid #f1f5f9', borderRadius: 2 }}>
              <Stack spacing={1}>
                {STATUS_OPTIONS.map((opt) => {
                  const cfg = STATUS_CONFIG[opt]
                  const isActive = job.status === opt
                  return (
                    <Button key={opt} fullWidth size="small" variant={isActive ? 'contained' : 'outlined'}
                      startIcon={cfg.icon}
                      disabled={isActive || setStatus.isPending}
                      onClick={() => { setStatus.mutate({ id: job.id, status: opt }); analytics.track(EVENTS.JOB_STATUS_CHANGED, { job_id: job.id, status: opt }) }}
                      sx={{
                        justifyContent: 'flex-start', borderRadius: 1.5, fontWeight: 600, fontSize: 12,
                        ...(isActive
                          ? { bgcolor: cfg.color, color: '#fff', '&.Mui-disabled': { bgcolor: cfg.color, color: '#fff', opacity: 0.8 } }
                          : { borderColor: cfg.border, color: cfg.color, '&:hover': { bgcolor: cfg.bg, borderColor: cfg.color } }
                        ),
                      }}>
                      {cfg.label}
                    </Button>
                  )
                })}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: '#64748b' }}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Create Job Dialog ────────────────────────────────────────────────

function CreateJobDialog({ open, onClose, devices, users }: {
  open: boolean; onClose: () => void; devices: Device[]; users: User[]
}) {
  const { t } = useTranslation()
  const create = useCreateJob()
  const [form, setForm] = useState({
    reference: '', customer_name: '', customer_phone: '',
    pickup_address: '', dropoff_address: '', notes: '',
    scheduled_at: null as Date | null,
    assigned_driver_id: '', assigned_device_id: '',
  })

  const handleCreate = async () => {
    const data: Record<string, unknown> = { ...form }
    if (form.scheduled_at) data.scheduled_at = form.scheduled_at.toISOString()
    else delete data.scheduled_at
    if (!form.assigned_driver_id) delete data.assigned_driver_id
    if (!form.assigned_device_id) delete data.assigned_device_id
    await create.mutateAsync(data as any)
    analytics.track(EVENTS.JOB_CREATED, { customer_name: form.customer_name, has_driver: !!form.assigned_driver_id, has_device: !!form.assigned_device_id })
    onClose()
    setForm({ reference: '', customer_name: '', customer_phone: '', pickup_address: '', dropoff_address: '', notes: '', scheduled_at: null, assigned_driver_id: '', assigned_device_id: '' })
  }

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [field]: e.target.value }))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Add sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        {t('fleet.createJob')}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mb: -1.5 }}>
            {t('jobs.title')}
          </Typography>
          <TextField label="Reference / Order #" size="small" fullWidth value={form.reference} onChange={set('reference')}
            placeholder="e.g. ORD-2024-0001"
            InputProps={{ startAdornment: <InputAdornment position="start"><Assignment sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />

          <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mb: -1.5 }}>
            {t('jobs.customerName')}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField label={t('jobs.customerName')} size="small" fullWidth value={form.customer_name} onChange={set('customer_name')}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
            <TextField label={t('jobs.customerPhone')} size="small" fullWidth value={form.customer_phone} onChange={set('customer_phone')}
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
          </Stack>

          <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mb: -1.5 }}>
            {t('jobs.pickupAddress')}
          </Typography>
          <TextField label={t('jobs.pickupAddress')} size="small" fullWidth value={form.pickup_address} onChange={set('pickup_address')}
            InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ fontSize: 16, color: '#10b981' }} /></InputAdornment> }} />
          <TextField label={t('jobs.dropoffAddress')} size="small" fullWidth value={form.dropoff_address} onChange={set('dropoff_address')}
            InputProps={{ startAdornment: <InputAdornment position="start"><Flag sx={{ fontSize: 16, color: '#ef4444' }} /></InputAdornment> }} />

          <Typography variant="overline" color="#94a3b8" fontWeight={700} sx={{ letterSpacing: '0.08em', mb: -1.5 }}>
            {t('jobs.assignDriver')}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField select label={t('jobs.assignDriver')} size="small" fullWidth value={form.assigned_driver_id}
              onChange={set('assigned_driver_id')} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('common.noData')}</MenuItem>
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
            <TextField select label={t('jobs.assignVehicle')} size="small" fullWidth value={form.assigned_device_id}
              onChange={set('assigned_device_id')} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">{t('common.noData')}</MenuItem>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker label={t('jobs.scheduledAt')} value={form.scheduled_at}
              onChange={(v) => setForm((p) => ({ ...p, scheduled_at: v }))}
              slotProps={{ textField: { size: 'small', fullWidth: true } }} />
          </LocalizationProvider>

          <TextField label={t('jobs.notes')} size="small" fullWidth multiline rows={3} value={form.notes} onChange={set('notes')}
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}><Notes sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: '#64748b' }}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleCreate} disabled={create.isPending || !form.customer_name}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
          {create.isPending ? t('common.loading') : t('fleet.createJob')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Main Page ────────────────────────────────────────────────────────

export default function JobsPage() {
  const { t } = useTranslation()
  const { data: list = [], isLoading } = useJobs()
  const remove = useDeleteJob()
  const setStatus = useUpdateJobStatus()
  const { data: devicesRaw } = useDevices()
  const { data: usersRaw } = useUsers()
  const devices = unwrap<Device>(devicesRaw)
  const users = unwrap<User>(usersRaw)

  const [createOpen, setCreateOpen] = useState(false)
  const [detailJob, setDetailJob] = useState<Job | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tab, setTab] = useState(0)

  // Build lookups
  const driverMap = useMemo(() => new Map(users.map((u) => [u.id, u.name])), [users])
  const deviceMap = useMemo(() => new Map(devices.map((d) => [d.id, d.name])), [devices])

  // Counts per status
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: list.length }
    STATUS_OPTIONS.forEach((s) => { c[s] = list.filter((j) => j.status === s).length })
    return c
  }, [list])

  // Filter
  const filtered = useMemo(() => {
    let jobs = list
    const tabStatus = tab === 0 ? 'all' : STATUS_OPTIONS[tab - 1]
    const activeFilter = statusFilter !== 'all' ? statusFilter : tabStatus
    if (activeFilter !== 'all') jobs = jobs.filter((j) => j.status === activeFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      jobs = jobs.filter((j) =>
        j.reference?.toLowerCase().includes(q) ||
        j.customer_name?.toLowerCase().includes(q) ||
        j.customer_phone?.includes(q) ||
        j.pickup_address?.toLowerCase().includes(q) ||
        j.dropoff_address?.toLowerCase().includes(q) ||
        j.id.toLowerCase().includes(q)
      )
    }
    return jobs
  }, [list, search, statusFilter, tab])

  // KPI stats
  const stats = useMemo(() => ({
    total: list.length,
    active: list.filter((j) => j.status === 'assigned' || j.status === 'en_route').length,
    completed: list.filter((j) => j.status === 'completed').length,
    unassigned: list.filter((j) => j.status === 'pending' && !j.assigned_driver_id).length,
  }), [list])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('jobs.title')}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
            {t('jobs.subtitle')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5, fontWeight: 600, px: 3 }}>
          New Job
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('jobs.subtitle'), value: stats.total, icon: <Assignment />, color: '#34A8E0', bg: '#EBF6FD' },
          { label: t('jobs.inProgress'), value: stats.active, icon: <LocalShipping />, color: '#f59e0b', bg: '#fffbeb' },
          { label: t('jobs.completed'), value: stats.completed, icon: <CheckCircle />, color: '#10b981', bg: '#f0fdf4' },
          { label: t('jobs.pending'), value: stats.unassigned, icon: <PendingActions />, color: '#ef4444', bg: '#fff1f2' },
        ].map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Card sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#0f172a">{kpi.value}</Typography>
                <Typography variant="caption" color="#94a3b8" fontWeight={600} sx={{ fontSize: 11 }}>{kpi.label}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status Tabs */}
      <Card sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setStatusFilter('all') }}
          variant="scrollable" scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 48 },
            '& .Mui-selected': { color: '#34A8E0' },
            '& .MuiTabs-indicator': { bgcolor: '#34A8E0' },
          }}>
          <Tab label={<Badge badgeContent={counts.all} color="default" max={999} sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 18, minWidth: 18 } }}>{t('fleet.jobs')} &nbsp;&nbsp;</Badge>} />
          {STATUS_OPTIONS.map((s) => {
            const cfg = STATUS_CONFIG[s]
            return (
              <Tab key={s} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {cfg.icon}
                  {cfg.label}
                  <Chip label={counts[s] || 0} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }} />
                </Box>
              } />
            )
          })}
        </Tabs>
      </Card>

      {/* Search & Filters */}
      <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField size="small" placeholder={t('common.search') + '...'} value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="#94a3b8" fontWeight={600}>
            {filtered.length} of {list.length} jobs
          </Typography>
        </Stack>
      </Card>

      {/* Job Cards */}
      <Stack spacing={1.5}>
        {filtered.length === 0 && (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Assignment sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body1" fontWeight={600} color="#64748b">
              {list.length === 0 ? t('jobs.noJobs') : t('common.noData')}
            </Typography>
            <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
              {list.length === 0 ? t('jobs.subtitle') : t('common.search')}
            </Typography>
          </Card>
        )}
        {filtered.map((j) => {
          const s = STATUS_CONFIG[j.status] ?? STATUS_CONFIG.pending
          const driver = j.assigned_driver_id ? driverMap.get(j.assigned_driver_id) : null
          const device = j.assigned_device_id ? deviceMap.get(j.assigned_device_id) : null
          return (
            <Card key={j.id} sx={{
              p: 0, border: '1px solid #e2e8f0', borderRadius: 2,
              borderLeft: `4px solid ${s.color}`,
              transition: 'box-shadow 0.15s, transform 0.15s',
              '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)', transform: 'translateY(-1px)' },
            }}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                      <Typography variant="body1" fontWeight={700} color="#1e293b" noWrap>
                        {j.reference || `JOB-${j.id.slice(0, 8).toUpperCase()}`}
                      </Typography>
                      <Chip icon={s.icon} label={s.label} size="small" sx={{
                        bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
                        fontSize: 11, fontWeight: 600, height: 24,
                      }} />
                      {j.scheduled_at && (
                        <Chip icon={<Schedule sx={{ fontSize: 12 }} />} label={format(new Date(j.scheduled_at), 'MMM d, HH:mm')}
                          size="small" variant="outlined" sx={{ fontSize: 10, height: 22 }} />
                      )}
                    </Box>

                    <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
                      {j.customer_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="body2" color="#475569" fontWeight={500}>{j.customer_name}</Typography>
                        </Box>
                      )}
                      {j.customer_phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="body2" color="#475569">{j.customer_phone}</Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Route */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocationOn sx={{ fontSize: 14, color: '#10b981' }} />
                      <Typography variant="caption" color="#64748b" noWrap sx={{ maxWidth: 200 }}>
                        {j.pickup_address || '—'}
                      </Typography>
                      <ArrowForward sx={{ fontSize: 12, color: '#cbd5e1' }} />
                      <Flag sx={{ fontSize: 14, color: '#ef4444' }} />
                      <Typography variant="caption" color="#64748b" noWrap sx={{ maxWidth: 200 }}>
                        {j.dropoff_address || '—'}
                      </Typography>
                    </Box>

                    {/* Assignment & Time */}
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      {driver && (
                        <Chip icon={<Person sx={{ fontSize: 12 }} />} label={driver}
                          size="small" variant="outlined" sx={{ fontSize: 11, height: 22, borderColor: '#bfdbfe', color: '#3b82f6' }} />
                      )}
                      {device && (
                        <Chip icon={<DirectionsCar sx={{ fontSize: 12 }} />} label={device}
                          size="small" variant="outlined" sx={{ fontSize: 11, height: 22, borderColor: '#B3DCF2', color: '#34A8E0' }} />
                      )}
                      {!driver && !device && j.status === 'pending' && (
                        <Chip label="Unassigned" size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#fff1f2', color: '#ef4444', border: '1px solid #fecaca' }} />
                      )}
                      <Typography variant="caption" color="#cbd5e1" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <AccessTime sx={{ fontSize: 11 }} />
                        {formatDistanceToNow(new Date(j.created_at), { addSuffix: true })}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5} sx={{ ml: 2, flexShrink: 0 }}>
                    <Tooltip title={t('common.actions')}>
                      <IconButton size="small" onClick={() => setDetailJob(j)}
                        sx={{ color: '#34A8E0', bgcolor: '#EBF6FD', '&:hover': { bgcolor: '#ede9fe' } }}>
                        <Visibility sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {j.status !== 'completed' && j.status !== 'cancelled' && (
                      <TextField select size="small" value="" label=""
                        onChange={(e) => {
                          if (e.target.value) { setStatus.mutate({ id: j.id, status: e.target.value }); analytics.track(EVENTS.JOB_STATUS_CHANGED, { job_id: j.id, status: e.target.value }) }
                        }}
                        sx={{
                          minWidth: 42, '& .MuiSelect-select': { py: 0.75, px: 1 },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: () => <Edit sx={{ fontSize: 14, color: '#94a3b8' }} />,
                        }}>
                        {STATUS_OPTIONS.filter((o) => o !== j.status).map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {STATUS_CONFIG[opt].icon}
                              {STATUS_CONFIG[opt].label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    <Tooltip title={t('common.delete')}>
                      <IconButton size="small" onClick={() => setDeleteTarget(j.id)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: '#fff1f2' } }}>
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>
            </Card>
          )
        })}
      </Stack>

      {/* Dialogs */}
      <CreateJobDialog open={createOpen} onClose={() => setCreateOpen(false)} devices={devices} users={users} />
      {detailJob && <JobDetailDialog job={detailJob} onClose={() => setDetailJob(null)} devices={devices} users={users} />}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('common.delete')}
        message={t('jobs.noJobs')}
        onConfirm={() => { remove.mutate(deleteTarget!); setDeleteTarget(null) }}
        onClose={() => setDeleteTarget(null)}
        loading={remove.isPending}
      />
    </Box>
  )
}
